import { z } from 'zod';

// ---------- Invoices (Finance) with Authorize.net payment links ----------

export const INVOICE_STATUSES = ['draft', 'sent', 'viewed', 'partially_paid', 'paid', 'overdue', 'void', 'refunded'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
  refunded: 'Refunded',
};
/** Invoices that can still receive a payment. */
export const INVOICE_PAYABLE: readonly InvoiceStatus[] = ['sent', 'viewed', 'partially_paid', 'overdue'];

export const INVOICE_TERMS = ['due_on_receipt', 'net_7', 'net_15', 'net_30'] as const;
export type InvoiceTerms = (typeof INVOICE_TERMS)[number];
export const INVOICE_TERMS_LABELS: Record<InvoiceTerms, string> = { due_on_receipt: 'Due on receipt', net_7: 'Net 7', net_15: 'Net 15', net_30: 'Net 30' };
export const INVOICE_TERMS_DAYS: Record<InvoiceTerms, number> = { due_on_receipt: 0, net_7: 7, net_15: 15, net_30: 30 };

export interface InvoiceCompany {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  defaultTerms: InvoiceTerms;
  footerNote: string;
  /** Show the eCheck (bank account) option on the Authorize.net payment page. */
  allowECheck: boolean;
  /** Company bank accounts clients can wire / ACH to (full numbers: they are printed on invoices). */
  bankAccounts: InvoiceBankAccount[];
  defaultBankAccountId: string | null;
}

/** A company bank account shown on invoices for wire / ACH payment. */
export interface InvoiceBankAccount {
  id: string;
  label: string;
  beneficiaryName: string;
  bankName: string;
  bankAddress: string;
  accountNumber: string;
  routingAch: string;
  routingWire: string;
  swift: string;
  accountType: 'checking' | 'savings';
  currency: string;
  instructions: string;
}
export const DEFAULT_INVOICE_COMPANY: InvoiceCompany = {
  name: 'Everest Ventures Group LLC',
  addressLine1: '848 Brickell Avenue',
  addressLine2: '',
  city: 'Miami',
  state: 'Florida',
  zip: '33131',
  country: 'United States',
  phone: '',
  email: '',
  website: '',
  defaultTerms: 'due_on_receipt',
  footerNote: 'Thank you for your business.',
  allowECheck: false,
  bankAccounts: [],
  defaultBankAccountId: null,
};

/** ABA routing number: 9 digits with a valid checksum. */
export function isValidAbaRouting(v: string): boolean {
  if (!/^\d{9}$/.test(v)) return false;
  const d = v.split('').map(Number);
  return (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])) % 10 === 0;
}
/** SWIFT / BIC: 4 letters (bank), 2 letters (country), 2 alphanumerics (location), optional 3 alphanumerics (branch). */
export const isValidSwift = (v: string) => /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v);
export const maskAccount = (v: string | null | undefined) => (v ? `••${v.slice(-4)}` : '');

const id = z.string().uuid();
const text = (max = 5000) => z.string().trim().max(max);
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const cents = z.number().int().min(0).max(100_000_000);

export const invoiceItemInput = z.object({
  description: z.string().trim().min(1, 'Describe the product or service').max(300),
  details: text(1000).nullish(),
  quantity: z.number().positive().max(100_000),
  unitPriceCents: cents,
  /** Discount on this line, in cents (applied to quantity × price). */
  discountCents: cents.default(0),
});
export type InvoiceItemInput = z.infer<typeof invoiceItemInput>;

export const invoiceInput = z.object({
  clientId: id,
  sellerId: id.nullish(),
  enrollmentId: id.nullish(),
  billTo: z.object({
    name: z.string().trim().min(1, 'Required').max(200),
    email: z.string().trim().email().max(200).nullish().or(z.literal('')),
    phone: text(40).nullish(),
    address: text(500).nullish(),
  }),
  issueDate: dateOnly,
  terms: z.enum(INVOICE_TERMS).default('due_on_receipt'),
  dueDate: dateOnly,
  items: z.array(invoiceItemInput).min(1, 'Add at least one item').max(100),
  discountType: z.enum(['none', 'amount', 'percent']).default('none'),
  /** Cents when discountType = amount; basis points (10% = 1000) when percent. */
  discountValue: z.number().int().min(0).max(100_000_000).default(0),
  /** Tax rate in basis points (7% = 700). */
  taxRateBp: z.number().int().min(0).max(5000).default(0),
  notes: text(3000).nullish(),
  internalNotes: text(3000).nullish(),
  /** How the client may pay: card through the Authorize.net payment link, and/or bank wire / ACH. */
  payByCard: z.boolean().default(true),
  payByWire: z.boolean().default(false),
  /** Company bank account printed on the invoice (required when payByWire). */
  bankAccountId: z.string().uuid().nullish(),
}).superRefine((v, ctx) => {
  if (!v.payByCard && !v.payByWire) ctx.addIssue({ code: 'custom', path: ['payByCard'], message: 'Choose at least one way for the client to pay' });
  if (v.payByWire && !v.bankAccountId) ctx.addIssue({ code: 'custom', path: ['bankAccountId'], message: 'Choose the bank account for wire / ACH payments' });
});
export type InvoiceInput = z.infer<typeof invoiceInput>;

/** Refund or void an invoice payment (dashboard). */
export const invoiceRefundInput = z.object({
  amountCents: z.number().int().positive().max(100_000_000),
  reason: z.string().trim().min(3, 'Give a short reason').max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
  /** Manual payments only: reference of the refund you sent. */
  reference: z.string().trim().max(200).nullish(),
});

export const invoiceManualPaymentInput = z.object({
  amountCents: z.number().int().positive().max(100_000_000),
  receivedAt: z.string().min(10).max(40),
  method: z.string().trim().min(1).max(120),
  reference: z.string().trim().min(1, 'Add the transaction or check reference').max(200),
  bankAccount: text(200).nullish(),
});

const digits = (label: string, min: number, max: number) =>
  z.string().trim().transform((v) => v.replace(/[\s-]/g, '')).refine((v) => v === '' || new RegExp(`^\\d{${min},${max}}$`).test(v), `${label} must be ${min}–${max} digits`);
const routing = (label: string) =>
  z.string().trim().transform((v) => v.replace(/[\s-]/g, '')).refine((v) => v === '' || isValidAbaRouting(v), `${label} must be a valid 9-digit ABA routing number`);

export const invoiceBankAccountInput = z
  .object({
    id: z.string().uuid(),
    label: z.string().trim().min(1, 'Name this account').max(80),
    beneficiaryName: z.string().trim().min(1, 'Account holder name is required').max(200),
    bankName: z.string().trim().min(1, 'Bank name is required').max(200),
    bankAddress: text(300),
    accountNumber: digits('Account number', 4, 17).refine((v) => v !== '', 'Account number is required'),
    routingAch: routing('ACH routing number'),
    routingWire: routing('Wire routing number'),
    swift: z.string().trim().toUpperCase().refine((v) => v === '' || isValidSwift(v), 'SWIFT / BIC must be 8 or 11 characters'),
    accountType: z.enum(['checking', 'savings']).default('checking'),
    currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).default('USD'),
    instructions: text(500),
  })
  .refine((v) => v.routingAch || v.routingWire || v.swift, { path: ['routingWire'], message: 'Add a routing number or SWIFT code' });

export const invoiceSettingsInput = z.object({
  name: z.string().trim().min(1).max(200),
  addressLine1: text(200),
  addressLine2: text(200),
  city: text(100),
  state: text(100),
  zip: text(20),
  country: text(100),
  phone: text(60),
  email: z.string().trim().email().max(200).or(z.literal('')),
  website: text(200),
  defaultTerms: z.enum(INVOICE_TERMS),
  footerNote: text(500),
  allowECheck: z.boolean(),
  bankAccounts: z.array(invoiceBankAccountInput).max(5).default([]),
  defaultBankAccountId: z.string().uuid().nullish(),
});

export interface InvoiceTotals {
  lines: { grossCents: number; discountCents: number; amountCents: number }[];
  subtotalCents: number;
  lineDiscountCents: number;
  invoiceDiscountCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
}

/**
 * Invoice math, shared by the form (live totals), the API and the PDF so all three always agree.
 * Subtotal = Σ qty × price (rounded per line). Line discounts and the invoice discount reduce it; tax applies after.
 */
export function computeInvoiceTotals(input: { items: { quantity: number; unitPriceCents: number; discountCents?: number }[]; discountType?: 'none' | 'amount' | 'percent'; discountValue?: number; taxRateBp?: number }): InvoiceTotals {
  const lines = input.items.map((i) => {
    const gross = Math.round(i.quantity * i.unitPriceCents);
    const disc = Math.min(gross, Math.max(0, i.discountCents ?? 0));
    return { grossCents: gross, discountCents: disc, amountCents: gross - disc };
  });
  const subtotal = lines.reduce((s, l) => s + l.grossCents, 0);
  const lineDisc = lines.reduce((s, l) => s + l.discountCents, 0);
  const afterLines = subtotal - lineDisc;
  const t = input.discountType ?? 'none';
  const v = input.discountValue ?? 0;
  const invDisc = Math.min(afterLines, t === 'amount' ? v : t === 'percent' ? Math.round((afterLines * Math.min(v, 10_000)) / 10_000) : 0);
  const taxable = afterLines - invDisc;
  const tax = Math.round((taxable * (input.taxRateBp ?? 0)) / 10_000);
  return { lines, subtotalCents: subtotal, lineDiscountCents: lineDisc, invoiceDiscountCents: invDisc, discountCents: lineDisc + invDisc, taxCents: tax, totalCents: taxable + tax };
}

export const invoiceRef = (year: number, seq: number) => `EVG-${year}-${String(seq).padStart(5, '0')}`;
